import { memo } from "react";
import { Sparkles, Package, Layers, UserCircle, Library, BookOpen, User, LogIn } from "lucide-react";
import { cn } from "@/lib/utils";

// Brand tokens
const PURPLE = "#622066";
const INACTIVE = "rgba(57,60,68,0.5)"; // Slate at 50% opacity

interface MobileNavbarProps {
  isActive: (path: string) => boolean;
  handleNavigate: (path: string) => (e: React.MouseEvent) => void;
  showProtectedLinks: boolean;
}

// Checks if the "My Knits" section is active — covers the studio and individual project routes
function isKnitsActive(isActive: (path: string) => boolean, pathname?: string): boolean {
  return isActive('/studio') || (typeof window !== 'undefined' && window.location.pathname.startsWith('/project/'));
}

interface NavItemProps {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: (e: React.MouseEvent) => void;
}

/** Regular (non-elevated) nav item */
function NavItem({ label, icon, active, onClick }: NavItemProps) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center justify-end gap-0.5 px-3 py-2 transition-colors"
      style={{ color: active ? PURPLE : INACTIVE, minHeight: 48 }}
    >
      {icon}
      <span className="text-[10px] font-medium leading-none">{label}</span>
    </button>
  );
}

/** Elevated Create button — always filled purple, 8px taller, same bottom alignment */
function CreateNavItem({ active, onClick }: { active: boolean; onClick: (e: React.MouseEvent) => void }) {
  return (
    <button
      onClick={onClick}
      aria-label="Create new pattern"
      className="
        flex flex-col items-center justify-end gap-0.5
        px-4 pb-2 rounded-t-xl
        transition-opacity
      "
      style={{
        background: PURPLE,
        color: '#fff',
        minHeight: 56,          // 8px taller than siblings (48px)
        alignSelf: 'flex-end',  // same bottom edge as siblings
        opacity: active ? 1 : 0.92,
      }}
    >
      <Sparkles size={20} strokeWidth={2} />
      <span className="text-[10px] font-semibold leading-none">Create</span>
    </button>
  );
}

const MobileNavbarComponent = ({
  isActive,
  handleNavigate,
  showProtectedLinks,
}: MobileNavbarProps) => {
  const knitsActive = isKnitsActive(isActive);

  // Unauthenticated mobile nav: Library, Blog, About, Sign In
  if (!showProtectedLinks) {
    return (
      <nav
        aria-label="Mobile navigation"
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-yarn-quartz to-yarn-ivory border-t border-yarn-orchid/10 shadow-[0_-2px_10px_rgba(0,0,0,0.06)] backdrop-blur-sm"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex items-end justify-around px-2 pt-1">
          <NavItem
            label="Library"
            icon={<Library size={20} strokeWidth={2} />}
            active={isActive('/library')}
            onClick={handleNavigate("/library")}
          />
          <NavItem
            label="Blog"
            icon={<BookOpen size={20} strokeWidth={2} />}
            active={isActive('/blog')}
            onClick={handleNavigate("/blog")}
          />
          <NavItem
            label="About"
            icon={<User size={20} strokeWidth={2} />}
            active={isActive('/about')}
            onClick={handleNavigate("/about")}
          />
          <NavItem
            label="Sign In"
            icon={<LogIn size={20} strokeWidth={2} />}
            active={isActive('/login')}
            onClick={handleNavigate("/login")}
          />
        </div>
      </nav>
    );
  }

  // Authenticated mobile nav (unchanged)
  return (
    <nav
      aria-label="Mobile navigation"
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-yarn-quartz to-yarn-ivory border-t border-yarn-orchid/10 shadow-[0_-2px_10px_rgba(0,0,0,0.06)] backdrop-blur-sm"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex items-end justify-around px-2 pt-1">
        <NavItem
          label="Library"
          icon={<Library size={20} strokeWidth={2} />}
          active={isActive('/library')}
          onClick={handleNavigate("/library")}
        />

        <NavItem
          label="My Knits"
          icon={<Layers size={20} strokeWidth={2} />}
          active={knitsActive}
          onClick={handleNavigate("/studio")}
        />

        <CreateNavItem
          active={isActive('/pattern-generator')}
          onClick={handleNavigate("/pattern-generator")}
        />

        <NavItem
          label="My Stash"
          icon={<Package size={20} strokeWidth={2} />}
          active={isActive('/shelf')}
          onClick={handleNavigate("/shelf")}
        />

        <NavItem
          label="Account"
          icon={<UserCircle size={20} strokeWidth={2} />}
          active={isActive('/account')}
          onClick={handleNavigate("/account")}
        />
      </div>
    </nav>
  );
};

MobileNavbarComponent.displayName = 'MobileNavbar';
export const MobileNavbar = memo(MobileNavbarComponent);
